import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { NphiesEligibilityResultDto } from '@his/shared';
import { del, get, post, put } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { dateOnly, money } from '@/lib/utils';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { DataTable, Dialog, Tabs } from '@/components/ui/layout';
import { Alert, Badge, Button, DatePicker, Field, Input, Select, Spinner } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { EligibilityChip } from './EligibilityChip';
import type { CoverageRow } from './CoveragePage';
import { withReturnTo } from './insurance-routes';
import { patientTabPath } from '../patients/patient-tabs';
import { useEligibilitySession } from './flow/EligibilityPanel';
import { MessagesButton } from './flow/MessagesDrawer';
import { PatientInsuranceView, SessionChip, type PatientInsuranceView as InsuranceViewId } from './flow/PatientInsuranceSections';
import { PolicySearchDialog } from './flow/PolicySearchDialog';

function ReferenceDialog({ row, patientId, onClose }: { row: CoverageRow; patientId: string; onClose: () => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [reference, setReference] = useState(row.externalEligibilityRef ?? '');
  const [date, setDate] = useState(row.externalEligibilityDate ?? new Date().toISOString().slice(0, 10));
  const save = useMutation({
    mutationFn: () => put(`/insurance/coverages/${row.id}/external-eligibility`, { reference: reference.trim(), date }),
    onSuccess: () => {
      toast.success('Eligibility reference saved');
      void queryClient.invalidateQueries({ queryKey: ['patient-coverages', patientId] });
      onClose();
    },
    onError: (caught) => toast.error('Could not save it', describeError(caught)),
  });
  return (
    <Dialog
      open
      onClose={onClose}
      title="Eligibility reference (phone/portal)"
      description="The reference the payer gave when cover was confirmed by phone or on its portal"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={save.isPending} disabled={reference.trim().length < 2 || !date} onClick={() => save.mutate()}>
            Save
          </Button>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Reference" required>
          <Input value={reference} onChange={(e) => setReference(e.target.value)} />
        </Field>
        <Field label="Date" required>
          <DatePicker value={date} onChange={(value) => setDate(value)} />
        </Field>
      </div>
    </Dialog>
  );
}

function BenefitsDialog({ row, onClose }: { row: CoverageRow; onClose: () => void }) {
  return (
    <Dialog open wide onClose={onClose} title={`Benefits · ${row.payer.name}`} description="As the insurer answered the last policy check" footer={<Button variant="ghost" onClick={onClose}>Close</Button>}>
      <DataTable
        dense
        rows={row.benefits ?? []}
        rowKey={(benefit) => benefit.category}
        empty="The insurer listed no benefits."
        columns={[
          { header: 'Category', cell: (benefit) => benefit.category },
          { header: 'Co-pay', cell: (benefit) => [benefit.coPayPct !== null ? `${benefit.coPayPct}%` : null, benefit.coPayFixedMinor !== null ? money(benefit.coPayFixedMinor) : null].filter(Boolean).join(' + ') || '—' },
          { header: 'Co-pay cap', align: 'right', cell: (benefit) => (benefit.coPayMaxMinor === null ? '—' : `${money(benefit.coPayMaxMinor)}${benefit.maxPeriod ? ` per ${benefit.maxPeriod}` : ''}`) },
          { header: 'Approval limit', align: 'right', cell: (benefit) => (benefit.approvalLimitMinor === null ? '—' : money(benefit.approvalLimitMinor)) },
          { header: 'Allowed / used', align: 'right', cell: (benefit) => (benefit.allowedMinor === null ? '—' : `${money(benefit.usedMinor ?? 0)} of ${money(benefit.allowedMinor)}`) },
        ]}
      />
    </Dialog>
  );
}

interface FamilyPolicyRow {
  id: string;
  policyNo: string;
  relation: string;
  validFrom: string | null;
  validTo: string | null;
  planName: string | null;
  payer: { id: string; name: string };
  plan: { id: string; code: string; name: string } | null;
  patient: { id: string; mrn: string; fullName: string };
}

const HOLDER_RELATIONS = ['child', 'spouse', 'parent', 'other'] as const;

const HOLDER_RELATION_LABEL: Record<(typeof HOLDER_RELATIONS)[number], () => string> = {
  child: () => tr('billing', 'holder_child'),
  spouse: () => tr('billing', 'holder_spouse'),
  parent: () => tr('billing', 'holder_parent'),
  other: () => tr('billing', 'holder_other'),
};

function FamilyPolicyDialog({ patientId, onClose }: { patientId: string; onClose: () => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [coverageId, setCoverageId] = useState('');
  const [relation, setRelation] = useState<(typeof HOLDER_RELATIONS)[number]>('child');
  const [error, setError] = useState<string | null>(null);
  const policies = useQuery({
    queryKey: ['family-coverages', patientId],
    queryFn: () => get<FamilyPolicyRow[]>(`/insurance/patients/${patientId}/family-coverages`),
  });

  const save = useMutation({
    mutationFn: () => post('/insurance/coverages/from-family', { patientId, coverageId, relation }),
    onSuccess: () => {
      toast.success(tr('billing', 'policy_added_from_the_family'));
      void queryClient.invalidateQueries({ queryKey: ['patient-coverages', patientId] });
      void queryClient.invalidateQueries({ queryKey: ['patient', patientId] });
      onClose();
    },
    onError: (caught) => setError(describeError(caught)),
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title={tr('billing', 'use_a_family_members_policy')}
      description={tr('billing', 'copies_the_policy_number_and_plan_from_a_linked')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {tr('billing', 'cancel')}
          </Button>
          <Button
            loading={save.isPending}
            disabled={!coverageId}
            onClick={() => {
              setError(null);
              save.mutate();
            }}
          >
            {tr('billing', 'add_policy')}
          </Button>
        </>
      }
    >
      {policies.isLoading ? (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Spinner /> {tr('billing', 'loading_family_policies')}
        </p>
      ) : policies.isError ? (
        <Alert variant="destructive">{describeError(policies.error)}</Alert>
      ) : policies.data?.length === 0 ? (
        <Alert>{tr('billing', 'no_linked_family_member_has_an_active_policy')}</Alert>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={tr('billing', 'family_members_policy')} required className="sm:col-span-2">
            <Select value={coverageId} onChange={(e) => setCoverageId(e.target.value)}>
              <option value="">{tr('billing', 'choose')}…</option>
              {policies.data?.map((policy) => (
                <option key={policy.id} value={policy.id}>
                  {policy.patient.fullName} ({policy.patient.mrn}) · {policy.payer.name} · {policy.planName ?? '—'} · {policy.policyNo}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={tr('billing', 'this_patient_is_the_holders')} className="sm:col-span-2">
            <ChoiceButtons<(typeof HOLDER_RELATIONS)[number]>
              layout="chips"
              value={relation}
              onChange={setRelation}
              options={HOLDER_RELATIONS.map((value) => ({ value, label: HOLDER_RELATION_LABEL[value]() }))}
            />
          </Field>
          {error && (
            <div className="sm:col-span-2">
              <Alert variant="destructive">{error}</Alert>
            </div>
          )}
        </div>
      )}
    </Dialog>
  );
}

function PolicyList({ patientId }: { patientId: string }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { can, hasModule } = useAuth();
  const navigate = useNavigate();
  const returnTo = patientTabPath(patientId, 'insurance');
  const [referenceFor, setReferenceFor] = useState<CoverageRow | null>(null);
  const [benefitsOf, setBenefitsOf] = useState<CoverageRow | null>(null);
  const [discovering, setDiscovering] = useState(false);
  const [fromFamily, setFromFamily] = useState(false);
  const mayAskNphies = hasModule('nphies') && can('nphies.eligibility');
  const coverages = useQuery({ queryKey: ['patient-coverages', patientId], queryFn: () => get<CoverageRow[]>(`/insurance/patients/${patientId}/coverages`) });
  const sessions = useEligibilitySession(patientId, undefined, hasModule('nphies'));
  const sessionOf = new Map((sessions.data?.items ?? []).map((session) => [session.coverageId, session]));
  const mayEdit = can('coverage.verify');
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['patient-coverages', patientId] });

  const act = useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'confirm' | 'verify' | 'deactivate' }) =>
      action === 'deactivate' ? del(`/insurance/coverages/${id}`) : post(`/insurance/coverages/${id}/${action}`, {}),
    onSuccess: () => refresh(),
    onError: (caught) => toast.error('Could not do that', describeError(caught)),
  });
  const check = useMutation({
    mutationFn: (coverageId: string) => post<NphiesEligibilityResultDto>('/integrations/nphies/eligibility', { patientId, coverageId }),
    onSuccess: (answer) => {
      if (answer.eligible) toast.success(answer.message, answer.warnings.join(' ') || undefined);
      else toast.error(answer.message, answer.warnings.join(' ') || undefined);
      refresh();
    },
    onError: (caught) => toast.error('The insurer could not be asked', describeError(caught)),
  });

  return (
    <div className="space-y-3">
      {(mayEdit || mayAskNphies) && (
        <div className="flex justify-end gap-2">
          {mayAskNphies && (
            <Button variant="outline" onClick={() => setDiscovering(true)}>
              {tr('billing', 'nx_has_insurance_no_policy')}
            </Button>
          )}
          {mayEdit && (
            <Button variant="outline" onClick={() => setFromFamily(true)}>
              {tr('billing', 'use_a_family_members_policy')}
            </Button>
          )}
          {mayEdit && <Button onClick={() => navigate(withReturnTo(`/patients/${patientId}/coverages/new`, returnTo))}>Add a policy</Button>}
        </div>
      )}
      <DataTable
        rows={coverages.data}
        loading={coverages.isLoading} refreshing={coverages.isFetching}
        error={coverages.error}
        rowKey={(row) => row.id}
        empty="No policy is recorded for this patient."
        columns={[
          { header: 'Payer', cell: (row) => row.payer.name },
          { header: 'Plan', cell: (row) => row.plan?.name ?? '—' },
          { header: 'Member', cell: (row) => <span className="font-mono text-xs">{row.policyNo}</span> },
          { header: 'Valid', cell: (row) => (row.validFrom || row.validTo ? `${row.validFrom ? dateOnly(row.validFrom) : '…'} – ${row.validTo ? dateOnly(row.validTo) : '…'}` : 'Open') },
          { header: 'Limit used', align: 'right', cell: (row) => (row.annualLimitMinor === null ? money(row.consumedMinor) : `${money(row.consumedMinor)} of ${money(row.annualLimitMinor)}`) },
          {
            header: 'Checked',
            cell: (row) =>
              <span className="flex flex-col gap-0.5">
                {sessionOf.get(row.id) ? <SessionChip session={sessionOf.get(row.id) as NonNullable<ReturnType<typeof sessionOf.get>>} /> : <EligibilityChip coverage={row} />}
                {row.externalEligibilityRef ? (
                  <span className="text-xs">Ref {row.externalEligibilityRef} · {row.externalEligibilityDate ? dateOnly(row.externalEligibilityDate) : ''}</span>
                ) : row.eligibilityAt ? (
                  <span className="text-xs text-muted-foreground">{dateOnly(row.eligibilityAt)}</span>
                ) : row.verifiedAt ? (
                  <span className="text-xs text-muted-foreground">{dateOnly(row.verifiedAt)}</span>
                ) : null}
              </span>,
          },
          { header: '', cell: (row) => (row.isActive ? <Badge variant="success">Active</Badge> : <Badge variant="muted">{row.source === 'nphies_discovery' ? 'Proposed' : 'Inactive'}</Badge>) },
          { header: '', cell: (row) => <MessagesButton entityType="patient_coverage" entityId={row.id} title={tr('billing', 'nx_messages_for_eligibility')} /> },
          {
            header: '',
            align: 'right',
            cell: (row) =>
              mayEdit && (
                <span className="flex justify-end gap-1">
                  <Button size="sm" variant="ghost" onClick={() => navigate(withReturnTo(`/patients/${patientId}/coverages/${row.id}/edit`, returnTo))}>
                    Edit
                  </Button>
                  {!row.isActive && (
                    <Button size="sm" variant="outline" onClick={() => act.mutate({ id: row.id, action: 'confirm' })}>
                      Use this policy
                    </Button>
                  )}
                  {row.isActive && mayAskNphies && (
                    <Button size="sm" variant="outline" loading={check.isPending && check.variables === row.id} onClick={() => check.mutate(row.id)}>
                      Check with NPHIES
                    </Button>
                  )}
                  {(row.benefits?.length ?? 0) > 0 && (
                    <Button size="sm" variant="ghost" onClick={() => setBenefitsOf(row)}>
                      Benefits
                    </Button>
                  )}
                  {row.isActive && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => act.mutate({ id: row.id, action: 'verify' })}>
                        Mark verified
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setReferenceFor(row)}>
                        Reference
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => act.mutate({ id: row.id, action: 'deactivate' })}>
                        Stop
                      </Button>
                    </>
                  )}
                </span>
              ),
          },
        ]}
      />
      {referenceFor &&<ReferenceDialog row={referenceFor} patientId={patientId} onClose={() => setReferenceFor(null)} />}
      {benefitsOf && <BenefitsDialog row={benefitsOf} onClose={() => setBenefitsOf(null)} />}
      {discovering && (
        <PolicySearchDialog
          patientId={patientId}
          payerIdsWithPolicy={(coverages.data ?? []).filter((row) => row.isActive).map((row) => row.payer.id)}
          onClose={() => setDiscovering(false)}
          onDone={() => setDiscovering(false)}
        />
      )}
      {fromFamily && <FamilyPolicyDialog patientId={patientId} onClose={() => setFromFamily(false)} />}
    </div>
  );
}

const VIEWS: { id: InsuranceViewId; label: () => string }[] = [
  { id: 'policies', label: () => tr('billing', 'nx_view_policies') },
  { id: 'history', label: () => tr('billing', 'nx_view_eligibility_history') },
  { id: 'preauths', label: () => tr('billing', 'nx_view_preauths') },
  { id: 'claims', label: () => tr('billing', 'nx_view_claims') },
];

export function PatientInsuranceTab({ patientId }: { patientId: string }) {
  const { can, hasModule } = useAuth();
  const [view, setView] = useState<InsuranceViewId>('policies');
  const mayRead = can('claim.read', 'preauth.manage', 'coverage.verify', 'invoice.read', 'nphies.eligibility');
  const visible = VIEWS.filter((entry) => entry.id === 'policies' || (mayRead && (entry.id !== 'history' || hasModule('nphies'))));
  return (
    <div className="space-y-3">
      {visible.length > 1 && <Tabs active={view} onChange={(id) => setView(id as InsuranceViewId)} help={false} tabs={visible.map((entry) => ({ id: entry.id, label: entry.label() }))} />}
      {view === 'policies' ? <PolicyList patientId={patientId} /> : <PatientInsuranceView view={view} patientId={patientId} />}
    </div>
  );
}
